import React, { useEffect, useState } from "react";
import {
  initialNodeStyle,
  initialDirectedEdgeStyle,
  initialUndirectedEdgeStyle,
  visitedNodeStyle,
  visitedDirectedEdgeStyle,
  visitedUndirectedEdgeStyle,
  visitingNodeStyle,
} from "./GraphStyle";
import "./BFSAlgorithm.css";
import * as d3 from "d3";

const BFSAlgorithm = ({ cytoscape, nodes, isDirected }) => {
  const [visitedNodes, setVisitedNodes] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentNode, setCurrentNode] = useState(null);
  const [logMessages, setLogMessages] = useState([]);
  const [queue, setQueue] = useState([]);

  const initialEdgeStyleToApply = isDirected
    ? initialDirectedEdgeStyle
    : initialUndirectedEdgeStyle;

  const visitedEdgeStyleToApply = isDirected
    ? visitedDirectedEdgeStyle
    : visitedUndirectedEdgeStyle;

  useEffect(() => {
    // Clear visited nodes when the graph type changes
    setVisitedNodes([]);
  }, [isDirected]);

  const animateBFS = async () => {
    //console.log("Animating BFS - Start");
    if (isAnimating) return;

    setIsAnimating(true);
    // Sort nodes by their IDs in ascending order
    const sortedNodes = [...nodes].sort(
      (a, b) => parseInt(a.data.id) - parseInt(b.data.id)
    );

    // Initialize a set to track visited nodes
    const visitedSet = new Set();
    const queue = []; // Initialize the queue

    for (const startNode of sortedNodes) {
      if (!visitedSet.has(startNode.data.id)) {
        const visitMessage = `Adding ${startNode.data.id} to queue`;
        setLogMessages((prevMessages) => [...prevMessages, visitMessage]);
        queue.push(startNode.data); // Add the start node to the queue
        setQueue([...queue]);
        visitedSet.add(startNode.data.id); // Mark the start node as visited

        while (queue.length > 0) {
          const currentNodeData = queue.shift();
          setCurrentNode(currentNodeData); // Set the current visiting node
          const visitMessage = `Visiting ${currentNodeData.id}`;
          const currentNode = cytoscape.getElementById(currentNodeData.id);
          currentNode.style(visitingNodeStyle);
          setLogMessages((prevMessages) => [...prevMessages, visitMessage]);

          await new Promise((resolve) => setTimeout(resolve, 1000));

          const adjacentEdges = Array.from(
            cytoscape.edges(`[source="${currentNodeData.id}"]`)
          );

          adjacentEdges.sort((edgeA, edgeB) => {
            const targetIdA = edgeA.target().id();
            const targetIdB = edgeB.target().id();

            if (targetIdA < targetIdB) return -1;
            if (targetIdA > targetIdB) return 1;
            return 0; // If equal
          });

          for (const edge of adjacentEdges) {
            const adjacentNodeId = edge.target().id();
            if (!visitedSet.has(adjacentNodeId)) {
              const adjacentNode = sortedNodes.find(
                (node) => node.data.id === adjacentNodeId
              );
              queue.push(adjacentNode.data);
              visitedSet.add(adjacentNodeId); // Mark the node as visited

              edge.style(
                isDirected
                  ? visitedDirectedEdgeStyle
                  : visitedUndirectedEdgeStyle
              );

              const addMessage = `Adding ${adjacentNodeId} to queue`;
              setLogMessages((prevMessages) => [...prevMessages, addMessage]);
            }
          }
          setQueue([...queue]);
          await new Promise((resolve) => setTimeout(resolve, 1000));
          currentNode.style(visitedNodeStyle);
          // Color the visited node and add it to the queue
          setVisitedNodes((prevVisitedNodes) => [
            ...prevVisitedNodes,
            currentNodeData,
          ]);
        }
      }
    }

    setIsAnimating(false);
    //console.log("BFS traversal completed");
  };

  const resetAnimation = () => {
    if (isAnimating) return;
    setLogMessages([]);

    // Reset styles of visited nodes
    const visitedNodeElements = cytoscape.elements(
      visitedNodes
        .map((visitedNode) => `node[id="${visitedNode.id}"]`)
        .join(", ")
    );
    visitedNodeElements.style(initialNodeStyle); // Reset to initialNodeStyle

    // Reset styles of visited edges
    const visitedEdgeElements = cytoscape.elements(
      visitedNodes
        .map((visitedNode) => `edge[source="${visitedNode.id}"]`)
        .join(", ")
    );
    visitedEdgeElements.style(
      isDirected ? initialDirectedEdgeStyle : initialUndirectedEdgeStyle
    ); // Reset to initialDirectedEdgeStyle

    setVisitedNodes([]);
    setIsAnimating(false);
  };

  useEffect(() => {
    // Initialize visitedNodes with an empty array
    setVisitedNodes([]);
  }, []);

  // Apply the styles when Cytoscape is ready
  useEffect(() => {
    if (cytoscape) {
      const stylesheet = [
        {
          selector: "node",
          style: {
            ...initialNodeStyle,
          },
        },
        {
          selector: "edge",
          style: {
            ...initialEdgeStyleToApply,
          },
        },
        {
          selector: visitedNodes
            .map((visitedNode) => `node[id="${visitedNode.id}"]`)
            .join(", "),
          style: {
            ...visitedNodeStyle,
          },
        },
        {
          selector: visitedNodes
            .map((visitedNode) => `edge[source="${visitedNode.id}"]`)
            .join(", "),
          style: {
            ...visitedEdgeStyleToApply,
          },
        },
      ];

      cytoscape.style().fromJson(stylesheet).update();
    }
  }, [cytoscape, visitedNodes]);

  return (
    <div>
      <div className="button-container">
        <button
          className="animate-reset-button"
          onClick={animateBFS}
          disabled={isAnimating}
        >
          {isAnimating ? "Animating..." : "Animate BFS"}
        </button>
        &nbsp;&nbsp;
        <button
          className="animate-reset-button"
          onClick={resetAnimation}
          disabled={isAnimating || visitedNodes.length === 0}
        >
          Reset Animation
        </button>
      </div>
      <div>
        {/* Display the queue */}
        <div className="queue-container">
          <div className="queue-title">Queue:</div> &nbsp;
          <ul className="queue">
            {queue.map((item, index) => (
              <li key={index}>{item.id}</li>
            ))}
          </ul>
        </div>
      </div>
      {/*
      <div>
        <div className="log-messages">
          {logMessages.map((message, index) => (
            <div key={index}>{message}</div>
          ))}
        </div>
      </div>
      */}
    </div>
  );
};

export default BFSAlgorithm;
